import { supabase } from '../../shared/supabase/supabaseClient';
import {
  mapCustomerPackageEntitlement,
  mapServicePackage,
  type CustomerPackageEntitlement,
  type PurchaseServicePackageResult,
  type ServicePackage,
} from '../domain/servicePackageTypes';

function parseJsonArray(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data) as unknown;
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

export async function listServicePackages(
  includeInactive = false,
  branchId?: string | null,
): Promise<ServicePackage[]> {
  const { data, error } = await supabase.rpc('list_service_packages', {
    p_include_inactive: includeInactive,
    p_branch_id: branchId?.trim() || null,
  });
  if (error) throw new Error(error.message);
  return parseJsonArray(data)
    .map(mapServicePackage)
    .filter((x): x is ServicePackage => x !== null);
}

export async function createServicePackage(input: {
  name: string;
  price: number;
  sessionCount: number;
  productIds: string[];
  validityDays?: number;
  sellOnPortal?: boolean;
  branchId?: string | null;
}): Promise<{ packageId: string }> {
  const { data, error } = await supabase.rpc('create_service_package', {
    p_name: input.name.trim(),
    p_price: input.price,
    p_session_count: input.sessionCount,
    p_product_ids: input.productIds,
    p_validity_days: input.validityDays ?? 180,
    p_branch_id: input.branchId ?? null,
    p_sell_on_portal: input.sellOnPortal ?? false,
  });
  if (error) throw new Error(error.message);
  const packageId = (data as { package_id?: string } | null)?.package_id;
  if (!packageId) throw new Error('No se pudo crear el paquete.');
  return { packageId };
}

export async function updateServicePackage(input: {
  packageId: string;
  name?: string | null;
  price?: number | null;
  sessionCount?: number | null;
  productIds?: string[] | null;
  validityDays?: number | null;
  active?: boolean | null;
  sellOnPortal?: boolean | null;
}): Promise<void> {
  const { error } = await supabase.rpc('update_service_package', {
    p_package_id: input.packageId,
    p_name: input.name ?? null,
    p_price: input.price ?? null,
    p_session_count: input.sessionCount ?? null,
    p_product_ids: input.productIds ?? null,
    p_validity_days: input.validityDays ?? null,
    p_active: input.active ?? null,
    p_sell_on_portal: input.sellOnPortal ?? null,
  });
  if (error) throw new Error(error.message);
}

export async function purchaseServicePackage(input: {
  packageId: string;
  customerId: string;
  saleId?: string | null;
  branchId?: string | null;
  paymentMethodId?: string | null;
}): Promise<PurchaseServicePackageResult> {
  const { data, error } = await supabase.rpc('purchase_service_package', {
    p_package_id: input.packageId,
    p_customer_id: input.customerId,
    p_sale_id: input.saleId ?? null,
    p_branch_id: input.branchId?.trim() || null,
    p_payment_method_id: input.paymentMethodId?.trim() || 'cash',
  });
  if (error) throw new Error(error.message);
  const row = data as {
    ok?: boolean;
    idempotent?: boolean;
    entitlement_id?: string;
    sessions_remaining?: number;
    expires_at?: string;
    price?: number;
    sale_id?: string;
    status?: string;
    requires_payment?: boolean;
  } | null;
  const entitlementId = row?.entitlement_id;
  if (!entitlementId) throw new Error('No se pudo vender el paquete.');
  return {
    ok: row?.ok !== false,
    entitlementId,
    sessionsRemaining: Number(row?.sessions_remaining ?? 0),
    expiresAt: String(row?.expires_at ?? ''),
    price: Number(row?.price ?? 0),
    saleId: typeof row?.sale_id === 'string' ? row.sale_id : null,
    status: typeof row?.status === 'string' ? row.status : undefined,
    requiresPayment: row?.requires_payment === true,
    idempotent: row?.idempotent === true,
  };
}

export async function voidPackageEntitlement(input: {
  entitlementId: string;
  reason: string;
  voidSale?: boolean;
}): Promise<{ saleVoided: boolean }> {
  const { data, error } = await supabase.rpc('void_package_entitlement', {
    p_entitlement_id: input.entitlementId,
    p_reason: input.reason.trim(),
    p_void_sale: input.voidSale ?? true,
  });
  if (error) throw new Error(error.message);
  const row = data as { ok?: boolean; sale_voided?: boolean } | null;
  if (row?.ok === false) throw new Error('No se pudo anular el paquete.');
  return { saleVoided: row?.sale_voided === true };
}

export async function listCustomerPackageEntitlements(input?: {
  customerId?: string | null;
  includeInactive?: boolean;
}): Promise<CustomerPackageEntitlement[]> {
  const { data, error } = await supabase.rpc('list_customer_package_entitlements', {
    p_customer_id: input?.customerId?.trim() || null,
    p_include_inactive: input?.includeInactive ?? false,
  });
  if (error) throw new Error(error.message);
  return parseJsonArray(data)
    .map(mapCustomerPackageEntitlement)
    .filter((x): x is CustomerPackageEntitlement => x !== null);
}
